import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import { useUserRole } from '../hooks/useUserRole';
import DropdownMenu from './DropdownMenu';
import MobileNav from './MobileNav';

import {
  Buildings,
  CaretDown,
  ChartBar,
  Clipboard,
  Crown,
  FileText,
  FlowArrow,
  GearSix,
  GitBranch,
  House,
  Lightning,
  Microphone,
  Question,
  Robot,
  Scales,
  SignOut,
  Target,
  TrendUp,
  UsersThree
} from '@phosphor-icons/react';
const NewHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { plan: tier } = usePlan();
  const { activeOrganization } = useOrganization();
  const { isAdmin } = useUserRole();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isPartnerPro = tier === 'partner-pro' || tier === 'partner_pro';

  // Navigation structure
  const aiToolsItems = [
    { name: 'AI Workforce', path: '/partner-pro/ai-workforce/agents', icon: Lightning },
    { name: 'My Agents', path: '/partner-pro/ai-workforce/agents', icon: Robot },
    { name: 'Voice Agents', path: '/settings/voice', icon: Microphone },
    { name: 'Agent Builder', path: '/unified-agent-builder', icon: GitBranch }
  ];

  const processesItems = [
    { name: 'Assessments', path: '/assessments/wizard', icon: Clipboard },
    { name: 'Processes', path: '/processes', icon: FlowArrow },
    { name: 'Opportunities', path: '/opportunities', icon: Target },
    { name: 'Org Chart', path: '/org-chart', icon: GitBranch }
  ];

  const organisationItems = [
    { name: 'Org Settings', path: '/settings/admin', icon: GearSix },
    ...(isPartnerPro ? [{ name: 'Org Admin', path: '/settings/admin', icon: Buildings }] : []),
    { name: 'Team Members', path: '/settings/team', icon: UsersThree }
  ];

  const analyticsItems = [
    { name: 'AI Readiness', path: '/analytics', icon: TrendUp },
    { name: 'Usage Analytics', path: '/analytics', icon: ChartBar },
    { name: 'Voice Analytics', path: '/analytics/voice', icon: Microphone },
    { name: 'Agent Analytics', path: '/partner-pro/ai-workforce/analytics', icon: Robot }
  ];

  const templatesItems = [
    { name: 'Process Templates', path: '/process-templates', icon: FileText },
    { name: 'Opportunity Templates', path: '#', icon: Target },
    { name: 'Agent Templates', path: '#', icon: Robot }
  ];

  const moreItems = [
    { name: 'Account Settings', path: '/account', icon: GearSix },
    { name: 'Help & Support', path: '/contact', icon: Question },
    { name: 'Legal', path: '/terms', icon: Scales }
  ];

  const getTierBadge = () => {
    if (!tier) return null;
    
    const badges = {
      'basic': { color: 'from-[#718096] to-[#5C6778]', label: 'Basic' },
      'pro': { color: 'from-[#6366F1] to-[#8B5CF6]', label: 'Pro' },
      'partner-pro': { color: 'from-[#14F195] to-[#10B981]', label: 'Partner Pro' },
      'partner_pro': { color: 'from-[#14F195] to-[#10B981]', label: 'Partner Pro' }
    };
    
    const badge = badges[tier] || badges.basic;
    
    return (
      <span className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${badge.color} text-white text-xs font-semibold`}>
        {(tier === 'partner-pro' || tier === 'partner_pro') && <Crown size={12} />}
        {badge.label}
      </span>
    );
  };

  // Don't show header on public/landing pages
  const publicPaths = ['/', '/features', '/login', '/register', '/forgot-password', '/reset-password', '/about', '/contact', '/pricing', '/terms', '/privacy', '/cookie-policy', '/cookie-settings', '/ai-disclaimer', '/consultants', '/agencies', '/partner', '/businesses'];
  if (publicPaths.some(path => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  })) {
    return null;
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'glass border-b border-[rgba(255,255,255,0.1)]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg blur-md opacity-70"></div>
              <div className="relative bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] p-2 rounded-lg">
                <Lightning size={20} className="text-white" strokeWidth={2.5} />
              </div>
            </div>
            <div>
              <span className="text-sm sm:text-base font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <span className="text-white">MAN</span>
                <span className="text-[#EC4899] mx-1">&</span>
                <span className="text-white">MACHINE</span>
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 px-4 py-2 text-white hover:text-[#14F195] transition-colors"
            >
              <House size={18} />
              <span className="font-medium">Dashboard</span>
            </Link>

            <DropdownMenu title="AI Tools" items={aiToolsItems} icon={Robot} />
            <DropdownMenu title="Processes" items={processesItems} icon={FlowArrow} />
            <DropdownMenu title="Organisation" items={organisationItems} icon={Buildings} />
            <DropdownMenu title="Analytics" items={analyticsItems} icon={ChartBar} />
            <DropdownMenu title="Templates" items={templatesItems} icon={FileText} />
            <DropdownMenu title="More" items={moreItems} icon={CaretDown} />
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Tier Badge */}
            {getTierBadge()}

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <CaretDown size={16} className="text-white hidden md:block" />
              </button>

              {showProfileMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowProfileMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 glass rounded-2xl border border-[rgba(255,255,255,0.1)] shadow-2xl overflow-hidden z-50">
                    <div className="p-4 border-b border-[rgba(255,255,255,0.1)]">
                      <p className="text-white font-semibold">{user?.name || user?.email || 'User'}</p>
                      <p className="text-[#A0AEC0] text-sm">{activeOrganization?.name || 'No organization'}</p>
                    </div>
                    <Link
                      to="/account"
                      onClick={() => setShowProfileMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white transition-all"
                    >
                      <GearSix size={18} />
                      <span className="text-sm">Account</span>
                    </Link>
                    {/* Billing - Only for admins */}
                    {isAdmin && (
                      <Link
                        to="/billing"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-4 py-3 text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white transition-all"
                      >
                        <Crown size={18} />
                        <span className="text-sm">Billing</span>
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[#EF4444] hover:bg-[rgba(255,255,255,0.1)] transition-all border-t border-[rgba(255,255,255,0.1)]"
                    >
                      <SignOut size={18} />
                      <span className="text-sm">Sign Out</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Mobile Menu */}
            <MobileNav />
          </div>
        </div>
      </div>
    </header>
  );
};

export default NewHeader;
